/**
 * Componente shared pentru tabelul de note contabile decont — folosit
 * atat in editorul de decont (tab Note contabile), cat si in view-ul
 * Registru jurnal cross-deconturi de pe pagina-hub.
 */
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  )
}

export interface NotaContabila {
  id: number
  ordinal: number
  docd: string | null
  doct: string
  docn: string
  docns: string | null
  expl: string
  remarks: string
  condb: string
  concr: string
  suma: string
  tax: string
  part: string
  centru: string
  /** Documentul stins — la plata unei facturi din SPV, numărul ei. */
  docdec: string
  template: string
  moneda: string
  curs: string
}

export const TEMPLATE_OPTIONS = [
  '',
  'D4-Cheltuieli diverse-BF cu CUI',
  'Cumparare multipla -decont',
  'D7-Plata cu numerar facturi (chitanta)',
]

/** Input editabil inline intr-o celula de tabel — salveaza la blur/Enter. */
export function CellInput({
  value,
  onSave,
  align,
  type = 'text',
  minWidth,
}: {
  value: string
  onSave: (v: string) => void
  align?: 'right'
  type?: 'text' | 'date' | 'number'
  minWidth?: string
}) {
  return (
    <input
      key={value}
      type={type}
      defaultValue={value}
      onBlur={(e) => onSave(e.target.value.trim())}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
      }}
      style={minWidth ? { minWidth } : undefined}
      className={`w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-xs hover:border-b4l-border focus:border-b4l-border focus:bg-b4l-bg focus:outline-none ${
        align === 'right' ? 'text-right tabular-nums' : ''
      }`}
    />
  )
}

/** Select editabil inline intr-o celula — salveaza imediat la onChange. */
export function CellSelect({
  value,
  options,
  onSave,
}: {
  value: string
  options: string[]
  onSave: (v: string) => void
}) {
  return (
    <select
      value={value}
      onChange={(e) => onSave(e.target.value)}
      className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-xs hover:border-b4l-border focus:border-b4l-border focus:bg-b4l-bg focus:outline-none"
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt || '—'}
        </option>
      ))}
    </select>
  )
}

/**
 * Celulele EA ale unei note contabile (Data → Curs). NU include `#` ordinal
 * (chemat afara) sau butonul Sterge — astea-s adaugate de containerul caller
 * ca sa fie flexibil (view editor vs view registru).
 */
export function NotaRowCells({
  nota,
  invalidateKeys,
}: {
  nota: NotaContabila
  invalidateKeys: QueryKey[]
}) {
  const queryClient = useQueryClient()
  const invalidate = () => {
    for (const key of invalidateKeys) queryClient.invalidateQueries({ queryKey: key })
  }

  const patch = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await api.patch(`/deconturi/note-contabile/${nota.id}`, data)).data,
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const save = (field: keyof NotaContabila, v: string) => {
    if (v !== String(nota[field] ?? '')) patch.mutate({ [field]: v })
  }

  const docdValue = nota.docd ? nota.docd.slice(0, 10) : ''

  return (
    <>
      <td className="py-1 pr-2">
        <CellInput
          type="date"
          value={docdValue}
          onSave={(v) => {
            if (v !== docdValue) patch.mutate({ docd: v || null })
          }}
          minWidth="120px"
        />
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.doct} onSave={(v) => save('doct', v)} minWidth="70px" />
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.docn} onSave={(v) => save('docn', v)} minWidth="90px" />
      </td>
      <td className="py-1 pr-2 text-b4l-text-muted tabular-nums" style={{ minWidth: '120px' }}>
        {nota.docns ?? '—'}
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.expl} onSave={(v) => save('expl', v)} minWidth="140px" />
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.remarks} onSave={(v) => save('remarks', v)} minWidth="140px" />
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.condb} onSave={(v) => save('condb', v)} minWidth="90px" />
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.concr} onSave={(v) => save('concr', v)} minWidth="120px" />
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.part} onSave={(v) => save('part', v)} minWidth="140px" />
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.centru} onSave={(v) => save('centru', v)} minWidth="60px" />
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.docdec ?? ''} onSave={(v) => save('docdec', v)} minWidth="90px" />
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.tax} onSave={(v) => save('tax', v)} align="right" minWidth="50px" />
      </td>
      <td className="py-1 pr-2">
        <CellInput
          value={nota.suma}
          onSave={(v) => save('suma', v)}
          align="right"
          minWidth="80px"
        />
      </td>
      <td className="py-1 pr-2">
        <CellSelect
          value={nota.template}
          options={TEMPLATE_OPTIONS}
          onSave={(v) => save('template', v)}
        />
      </td>
      <td className="py-1 pr-2">
        <CellInput value={nota.moneda} onSave={(v) => save('moneda', v)} minWidth="50px" />
      </td>
      <td className="py-1 pr-2">
        <CellInput
          value={nota.curs}
          onSave={(v) => save('curs', v)}
          align="right"
          minWidth="60px"
        />
      </td>
    </>
  )
}

/** Header-ul standard al coloanelor EA (Data → Curs). */
export function NotaHeaderEA() {
  return (
    <>
      <th className="py-1 pr-2 font-medium">Data</th>
      <th className="py-1 pr-2 font-medium">Tip</th>
      <th className="py-1 pr-2 font-medium">Nr. doc</th>
      <th className="py-1 pr-2 font-medium">Nr.doc.sec.</th>
      <th className="py-1 pr-2 font-medium">Explicație</th>
      <th className="py-1 pr-2 font-medium">Descriere</th>
      <th className="py-1 pr-2 font-medium">Cont debit</th>
      <th className="py-1 pr-2 font-medium">Cont credit</th>
      <th className="py-1 pr-2 font-medium">Partener</th>
      <th className="py-1 pr-2 font-medium">Centru</th>
      <th className="py-1 pr-2 font-medium" title="Documentul stins: factura plătită">
        Doc. stins
      </th>
      <th className="py-1 pr-2 text-right font-medium">TVA%</th>
      <th className="py-1 pr-2 text-right font-medium">Sumă</th>
      <th className="py-1 pr-2 font-medium">Template</th>
      <th className="py-1 pr-2 font-medium">Md</th>
      <th className="py-1 pr-2 text-right font-medium">Curs</th>
    </>
  )
}
